import { useCallback, useEffect, useRef, useState } from "react";
import { IoSearch } from "react-icons/io5";
import { FaCircleCheck, FaCirclePlus } from "react-icons/fa6";
import { t } from "i18next";
import { v4 as uuidv4 } from "uuid";
import Keyboard from "simple-keyboard";
import "simple-keyboard/build/css/index.css";
import {
  osName,
  browserName,
  mobileModel,
  mobileVendor,
  deviceType,
} from "react-device-detect";
import Proximiio, { ImageDetection } from "proximiio-js-library";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import useMapStore from "@/store/mapStore";
import Feature from "proximiio-js-library/lib/models/feature";
import {
  cn,
  filterByAmenity,
  getFilteredItems,
  getSortedPOIs,
  pluralize,
} from "@/lib/utils";
import useFeaturesStore from "@/store/featuresStore";
import {
  excludedAmenities,
  filterCategories,
  getListAsClosest,
} from "@/store/data";
import React from "react";
import {
  arabicLayout,
  chineseLayout,
  englishLayout,
  keyboardsLayout,
  russianLayout,
  zhLayoutCandidates,
} from "@/lib/keyboardLayouts";
import { SortedPoiItemModel } from "@/models/sortedPoiItem.model";
import {
  FilterCategoryModel,
  FilterItemModel,
} from "@/models/filterItem.model";
import { OverlayScrollbarsComponentRef } from "overlayscrollbars-react";
import { Button } from "./ui/button";
import StopsContext from "./ui/stops";
import { StopModel } from "@/models/stop.model";
import { TbCurrentLocation, TbHome } from "react-icons/tb";
import centerOfMass from "@turf/center-of-mass";
import { FaSave } from "react-icons/fa";
import { postToNative } from "@/lib/nativeBridge";

function PoiSearch() {
  // store state
  const currentLang = useMapStore((state) => state.currentLang);
  const features = useFeaturesStore((state) => state.features);
  const kioskMode = useMapStore((state) => state.kioskMode);
  const appSession = useMapStore((state) => state.appSession);
  const activeKiosk = useMapStore((state) => state.activeKiosk);
  const routeFinish = useMapStore((state) => state.routeFinish);
  const mobileMode = useMapStore((state) => state.mobileMode);
  const floors = useMapStore((state) => state.floors);
  const open = useMapStore((state) => state.globalSearchOpen);
  const map = useMapStore((state) => state.map);
  const stopsBackup = useMapStore((state) => state.stopsBackup);
  const haveCustomPosition = useMapStore((state) => state.haveCustomPosition);
  const amenities = useMapStore((state) => state.amenities);
  const verticalKioskMode = useMapStore((state) => state.verticalKioskMode);
  const allowSave = useMapStore((state) => state.allowSave);

  // store actions
  const setRouteFinish = useMapStore((state) => state.setRouteFinish);
  const setActiveMenuItem = useMapStore((state) => state.setActiveMenuItem);
  const setActiveCategory = useMapStore((state) => state.setActiveCategory);
  const setOpen = useMapStore((state) => state.setGlobalSearchOpen);
  const centerToFinish = useMapStore((state) => state.centerToFinish);
  const setActiveFilter = useMapStore((state) => state.setActiveFilter);
  const setStopsStore = useMapStore((state) => state.setStops);
  const setStartFeature = useMapStore((state) => state.setStartFeature);
  const setCameraRecognitionFeature = useMapStore(
    (state) => state.setCameraRecognitionFeature,
  );
  const setNavMode = useMapStore((state) => state.setNavMode);
  const setShowCustomRoutePicker = useMapStore(
    (state) => state.setShowCustomRoutePicker,
  );

  const [search, setSearch] = useState("");
  const [filteredPois, setFilteredPois] = useState<SortedPoiItemModel[]>([]);
  const [foundInTitle, setFoundInTitle] = useState<SortedPoiItemModel[]>([]);
  const [foundInDescription, setFoundInDescription] = useState<
    SortedPoiItemModel[]
  >([]);

  const pois = getSortedPOIs({ features, currentLang, floors });
  const keyboard = useRef<Keyboard>();
  const listRef = useRef<HTMLDivElement>(null);
  const scrollRef =
    useRef<OverlayScrollbarsComponentRef<keyof JSX.IntrinsicElements>>(null);
  const scrollId = useRef<ReturnType<typeof setTimeout>>();
  const closestList = getListAsClosest();
  const multipointEnabled =
    import.meta.env.VITE_WAYFINDING_ENABLE_MULTIPOINT === "true" && !kioskMode;
  const combiNavEnabled =
    import.meta.env.VITE_WAYFINDING_ENABLE_COMBINAV === "true" && !kioskMode;
  const [enableGVision, setEnableGVision] = useState(
    import.meta.env.VITE_WAYDINDING_ENABLE_GVISION === "true",
  );

  const [stops, setStops] = useState<StopModel[]>(
    multipointEnabled
      ? stopsBackup.length > 0
        ? stopsBackup
        : [{ id: uuidv4() }]
      : [],
  );
  const [focusedStop, setFocusedStop] = useState<number | null>(null);

  const saveLog = useCallback(
    (feature?: Feature, poi?: SortedPoiItemModel) => {
      let featureAmenityCategory = "";
      const featureSection = feature
        ? filterCategories.find((category) =>
            category.items.find((item) => {
              if (
                (Array.isArray(item.id) &&
                  item.id.includes(feature.properties.amenity)) ||
                item.id === feature.properties.amenity
              ) {
                featureAmenityCategory = item.title;
                return true;
              }
            }),
          )
        : undefined;

      //save feature search log
      console.log("LOG: save feature search log");
      const userData = {
        osName,
        browserName,
        mobileModel,
        mobileVendor,
        deviceType:
          kioskMode && !mobileMode && activeKiosk?.name
            ? activeKiosk?.name
            : deviceType,
      };
      if (search.length > 0 || feature)
        new Proximiio.InteractionLogger({
          interactionType: "search",
          targetElementType: "feature",
          targetElementId: feature ? feature.id : undefined,
          targetElementTitle: feature ? feature.properties.title : undefined,
          targetElementAmenity: t(featureAmenityCategory, { lng: "en" }),
          targetElementAmenityCategory: featureSection
            ? t(featureSection.title, { lng: "en" })
            : undefined,
          searchValue: search,
          searchSuccess: !!feature,
          foundWithFuziness: poi?.matchedByFuzzy,
          fuzinessResult: poi?.fuzzyHighlight,
          kioskId: activeKiosk?.id ? activeKiosk?.id : undefined,
          kioskTitle: activeKiosk?.name ? activeKiosk?.name : undefined,
          metadata: userData,
          language: currentLang,
          session: appSession,
        });
    },
    [
      activeKiosk?.id,
      activeKiosk?.name,
      appSession,
      currentLang,
      search,
      kioskMode,
      mobileMode,
    ],
  );

  const destroyKeyboard = () => {
    if (keyboard.current?.activeButtonClass) {
      keyboard.current?.destroy();
      keyboard.current = {} as Keyboard;
      setSearch("");
    }
  };

  const onSelectHandle = useCallback(
    ({
      poi,
      cameraRecognized = false,
      defaultFeature,
    }: {
      poi: SortedPoiItemModel;
      cameraRecognized?: boolean;
      defaultFeature?: Feature;
    }) => {
      const feature = defaultFeature
        ? defaultFeature
        : features.find((item: Feature) => item.id === poi.id)!;
      if (multipointEnabled && stops.length > 1 && focusedStop !== null) {
        const updatedStops = [...stops];
        updatedStops[focusedStop].poi = poi;
        updatedStops[focusedStop].feature = feature;
        updatedStops[focusedStop].searchValue = poi.properties.title;
        if (updatedStops[focusedStop + 1]) {
          setFocusedStop(focusedStop + 1);
        }
        setStops(updatedStops);

        if (cameraRecognized) {
          const pathPointFeature = map.findPathPoint({
            featureId: updatedStops[0].feature!.id,
            displayOnMap: true,
          });
          map.centerToFeature(pathPointFeature.id);
          setCameraRecognitionFeature(pathPointFeature);
        }
      } else {
        destroyKeyboard();
        setOpen(false);

        if (cameraRecognized) {
          const pathPointFeature = map.findPathPoint({
            featureId: feature.id,
            displayOnMap: true,
          });
          map.centerToFeature(pathPointFeature.id);
          setCameraRecognitionFeature(pathPointFeature);
        } else {
          setRouteFinish(feature);
          centerToFinish();
        }
      }
      saveLog(feature, poi);
    },
    [
      features,
      multipointEnabled,
      stops,
      focusedStop,
      saveLog,
      setOpen,
      map,
      setCameraRecognitionFeature,
      setRouteFinish,
      centerToFinish,
    ],
  );

  const openChangeHandler = (open: boolean) => {
    saveLog();
    setOpen(open);
    setSearch("");
    setStops([{ id: uuidv4() }]);
  };
  useEffect(() => {
    if (mobileMode) {
      if (open) {
        document.body.style.setProperty("overflow", "auto", "important");
      } else {
        document.body.style.removeProperty("overflow");
      }
    }
  }, [open, mobileMode]);

  // store filtered pois in ref to ensure that filteredPoisRef.current always holds the latest value of filteredPois
  const filteredPoisRef = useRef<SortedPoiItemModel[]>([]);
  useEffect(() => {
    filteredPoisRef.current = filteredPois;
  }, [filteredPois]);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "f" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(!open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [setOpen, open]);

  useEffect(() => {
    const onChange = (input: string, e: MouseEvent | undefined) => {
      e?.stopPropagation();
      setSearch(input);
    };

    const onKeyPress = (button: string, e: MouseEvent | undefined) => {
      e?.stopPropagation();
      if (button === "{enter}") {
        if (filteredPoisRef.current.length > 0) {
          onSelectHandle({ poi: filteredPoisRef.current[0] });
        }
      }
      if (button === "{shift}" || button === "{lock}") {
        handleShift();
      }
      if (button === "{change-keyboard}") {
        handleKeyboardChange();
      }
      if (button === "{keyboard-en}") {
        handleLangChange("en");
      }
      if (button === "{keyboard-ar}") {
        handleLangChange("ar");
      }
      if (button === "{keyboard-zh}") {
        handleLangChange("zh");
      }
      if (button === "{keyboard-ru}") {
        handleLangChange("ru");
      }
      if (button === "{space}") {
        keyboard.current?.setOptions({
          layoutName: "shift",
        });
      }
      if (
        button !== "{space}" &&
        button !== "{bksp}" &&
        button !== "{change-keyboard}" &&
        button !== "{keyboard-en}" &&
        button !== "{keyboard-ar}" &&
        button !== "{keyboard-zh}" &&
        button !== "{keyboard-ru}"
      ) {
        keyboard.current?.setOptions({
          layoutName: "default",
        });
      }
      if (button === "{bksp}") {
        const lastLetter = keyboard.current?.getInput().slice(-1);
        const isUppercase =
          keyboard.current?.getInput().length === 1 ||
          (lastLetter && /^[A-Z]/.test(lastLetter));
        keyboard.current?.setOptions({
          layoutName: isUppercase ? "shift" : "default",
        });
      }
    };

    const handleShift = () => {
      const currentLayout = keyboard.current?.options.layoutName;
      const shiftToggle = currentLayout === "default" ? "shift" : "default";

      keyboard.current?.setOptions({
        layoutName: shiftToggle,
      });
    };

    const handleKeyboardChange = () => {
      keyboard.current?.setOptions({
        layout: keyboardsLayout,
      });
    };

    const handleLangChange = (language: string) => {
      let layout = englishLayout;
      if (language === "ar") layout = arabicLayout;
      if (language === "en") layout = englishLayout;
      if (language === "ru") layout = russianLayout;
      if (language === "zh") layout = chineseLayout;

      keyboard.current?.setOptions({
        layout,
        layoutCandidates: language === "zh" ? zhLayoutCandidates : undefined,
      });
    };

    const getLanguageLayout = () => {
      if (currentLang === "ar") return arabicLayout;
      if (currentLang === "en") return englishLayout;
      if (currentLang === "ru") return russianLayout;
      if (currentLang === "zh") return chineseLayout;
      return englishLayout;
    };

    if (open) {
      setActiveMenuItem("map");
    }

    if (
      kioskMode &&
      import.meta.env.VITE_WAYFINDING_USE_VIRTUAL_KEYBOARD_AT_KIOSKS ===
        "true" &&
      !mobileMode
    ) {
      if (open) {
        setFoundInTitle(
          filteredPoisRef.current.filter((item) => !item.foundInDescription),
        );
        if (!keyboard.current?.activeButtonClass) {
          console.log("should open keyboard");
          keyboard.current = new Keyboard({
            preventMouseDownDefault: true,
            onChange: (input, e) => onChange(input, e),
            onKeyPress: (button, e) => onKeyPress(button, e),
            layout: getLanguageLayout(),
            layoutCandidates:
              currentLang === "zh" ? zhLayoutCandidates : undefined,
            // rtl: currentLang === "ar",
            mergeDisplay: true,
            display: {
              "{enter}": currentLang === "ar" ? "\u2BA1" : "\u2BA0",
              "{bksp}": currentLang === "ar" ? "\uD83E\uDC26" : "\uD83E\uDC24",
              "{change-keyboard}": t("change-language"),
              "{keyboard-en}": "English keyboard",
              "{keyboard-ar}": `Arabic keyboard <br/> <small>لوحة المفاتيح العربية</small>`,
              /*"{keyboard-zh}": `Chinese keyboard <br/> <small>中文键盘</small>`,
              "{keyboard-ru}": `Russian keyboard <br/> <small>русская клавиатура</small>`,*/
            },
            maxLength: 20,
            layoutName: "shift",
          });
        }
      } else {
        console.log("should close keyboard");
        if (keyboard.current?.activeButtonClass) {
          keyboard.current?.destroy();
          keyboard.current = {} as Keyboard;
          setSearch("");
        }
      }
    }
  }, [
    open,
    kioskMode,
    filteredPois,
    onSelectHandle,
    currentLang,
    setActiveMenuItem,
    mobileMode,
    search,
  ]);

  useEffect(() => {
    const filtered = getFilteredItems({
      items: pois,
      search,
      language: currentLang,
    }) as SortedPoiItemModel[];

    setFilteredPois(filtered);
    setFoundInTitle(filtered.filter((item) => !item.foundInDescription));
    setFoundInDescription(filtered.filter((item) => item.foundInDescription));
  }, [search, currentLang, features, floors]);

  const setFilterCategoryHandler = (category: FilterCategoryModel) => {
    setActiveMenuItem("categories");
    setActiveCategory(category);
    setOpen(false);
  };

  const onSearchHandler = (value: string) => {
    setSearch(value);

    clearTimeout(scrollId.current);

    scrollId.current = setTimeout(() => {
      const osInstance = scrollRef?.current?.osInstance();

      if (!osInstance) {
        return;
      }
      const { scrollOffsetElement } = osInstance.elements();

      scrollOffsetElement.scrollTo({
        behavior: "instant",
        top: 0,
      });
    }, 100);
  };

  /*const onWifiHandler = () => {
    const wifi = filterCategories[
      filterCategories.findIndex((i) => i.id === "facilities")
    ].items.find((i) => i.title === "wifi");
    if (wifi) {
      destroyKeyboard();
      setActiveFilter(wifi as FilterItemModel);
      setOpen(false);
    }
  };*/

  /*const onClosestHandler = (item: FilterItemModel) => {
    if (item) {
      const closestFeature = map.getClosestFeature(
        item.id as string,
      ) as Feature;
      destroyKeyboard();
      setRouteFinish(closestFeature);
      centerToFinish();
      setOpen(false);
    }
  };*/

  const onSeeAllHandler = (item: FilterItemModel) => {
    if (item) {
      item.type = "list";
      setActiveFilter(item);
      setOpen(false);

      if (!mobileMode) {
        const filteredPois = filterByAmenity(pois, item.id);
        const centerPoint = centerOfMass({
          type: "FeatureCollection",
          features: filteredPois,
        });
        map.getMapboxInstance().jumpTo({
          zoom: 17,
          center: centerPoint.geometry.coordinates as [number, number],
        });
      }
    }
  };

  const getClosestList = () => {
    const normalize = (arr?: string[] | string[][]) =>
      Array.isArray(arr) ? arr.flat().map((i) => i.toLowerCase()) : [];

    const searchLower = search.toLowerCase();

    return closestList.filter((item) => {
      const amenityPois = features.filter(
        (i) => i.properties.amenity === item.id,
      );

      if (amenityPois.length > 0) {
        const keywordsSet = new Set<string>();
        const spellingsSet = new Set<string>();
        const titlesSet = new Set<string>();

        for (const poi of amenityPois) {
          const props = poi.properties;

          // Collect keywords
          normalize(props.keywords).forEach((k) => keywordsSet.add(k));
          normalize(props.keywords_i18n?.en).forEach((k) => keywordsSet.add(k));
          normalize(props.keywords_i18n?.ar).forEach((k) => keywordsSet.add(k));
          normalize(props.keywords_i18n?.ru).forEach((k) => keywordsSet.add(k));
          normalize(props.keywords_i18n?.zh).forEach((k) => keywordsSet.add(k));

          // Collect spellings
          normalize(props.spelling?.en).forEach((s) => spellingsSet.add(s));
          normalize(props.spelling?.ar).forEach((s) => spellingsSet.add(s));
          normalize(props.spelling?.ru).forEach((s) => spellingsSet.add(s));
          normalize(props.spelling?.zh).forEach((s) => spellingsSet.add(s));

          // Titles
          if (props.title) titlesSet.add(props.title.toLowerCase());
        }

        // Early exit if any term matches search
        const matches = [...keywordsSet, ...spellingsSet, ...titlesSet].some(
          (term) => term.includes(searchLower),
        );

        if (matches) return true;
      }

      // fallback to translated name match
      return `${t("nearest")} ${t(item.title)}`
        .toLowerCase()
        .includes(searchLower);
    });
  };

  const addStopHandler = () => {
    if (stops.length === 5) return;
    setStops([...stops, { id: uuidv4() }]);
    setFocusedStop(null);
    setTimeout(() => {
      const undefinedStopIndex = stops.findIndex((stop) => !stop.searchValue);
      setFocusedStop(undefinedStopIndex !== -1 ? undefinedStopIndex : 0);
    });
  };

  const removeStopHandler = (index: number) => {
    const newStops = [...stops];
    newStops.splice(index, 1);
    const undefinedStopIndex = newStops.findIndex((stop) => !stop.searchValue);
    setFocusedStop(undefinedStopIndex !== -1 ? undefinedStopIndex : 0);
    setStops([...newStops]);
  };

  const stopsChangedHandler = (stops: StopModel[]) => {
    const search = focusedStop !== null ? stops[focusedStop].searchValue : "";
    onSearchHandler(search || "");
    setStops([...stops]);
  };

  const stopFocusChangeHandler = (index: number | null) => {
    if (index !== null) {
      const search = stops[index].searchValue;
      onSearchHandler(search || "");
      setFocusedStop(index);
    }
  };

  const multipointDoneHandler = () => {
    const stopsWithFeature = stops.filter((stop) => stop.feature);
    if (stopsWithFeature.length === stops.length) {
      setStopsStore(stops);
      setRouteFinish(
        stops.length > 2 ? stops[0].feature! : stops[stops.length - 1].feature!,
      );
      //if (stops.length > 2) {
      setStartFeature(stops[0].feature!);
      //}
      centerToFinish();
      destroyKeyboard();
      setOpen(false);
      setStops([{ id: uuidv4() }]);
    }
  };

  const saveRouteHandler = () => {
    const stopsWithFeature = stops.filter((stop) => stop.feature);
    const destinations = stopsWithFeature.map((stop) => stop.feature!.id);
    if (stopsWithFeature.length === stops.length) {
      postToNative({
        type: "SAVE_ROUTE",
        payload: { destinations },
        time: Date.now(),
      });
    }
  };

  useEffect(() => {
    if (!enableGVision) {
      return;
    }
    if (navigator.mediaDevices) {
      navigator.mediaDevices.enumerateDevices().then(function (devices) {
        const videoDevices = devices.filter(
          (device) => device.kind === "videoinput",
        );
        setEnableGVision(videoDevices.length > 0);
      });
    } else {
      setEnableGVision(false);
    }
  }, [setEnableGVision, enableGVision]);

  const recognizeLocationHandler = () => {
    if (haveCustomPosition) {
      const feature = map
        .getMapState()
        .dynamicFeatures.features.find(
          (f: Feature) => f.id === "custom-position",
        );
      const pois = getSortedPOIs({
        features: [feature],
        currentLang,
        floors,
      });
      pois[0].properties.title = t("current-location");
      onSelectHandle({
        poi: pois[0],
        cameraRecognized: false,
        defaultFeature: feature,
      });
      return;
    }
    document.body.style.pointerEvents = "auto";
    ImageDetection.init(
      {
        gVisionApiKey: import.meta.env.VITE_WAYFINDING_GVISION_APIKEY,
        pois: getSortedPOIs({ features, currentLang, floors }).filter(
          (item) => {
            if (excludedAmenities.includes(item.properties.amenity))
              return false;
            return true;
          },
        ),
        cameraText: t("gvision-camera-text"),
        tryAgainButtonText: t("try-again"),
        confirmButtonText: t("confirm"),
        noResultsText: t("gvision-no-results"),
        resultsHeadingText: t("gvision-results-header"),
        returnResults: 1,
        dir: currentLang === "ar" ? "rtl" : "ltr",
      },
      (item) => {
        document.body.style.pointerEvents = "none";
        onSelectHandle({ poi: item, cameraRecognized: true });
      },
    );
  };

  const navigateFromHomeHandler = () => {
    setNavMode("city-to");
    setShowCustomRoutePicker(true);
    destroyKeyboard();
    setOpen(false);
  };

  return (
    <>
      <button
        className={cn(
          "absolute top-5 z-50 flex w-auto items-center rounded-full border-2 border-primary bg-white px-4 py-2 text-black outline-none transition-all hover:border-primary/70 group-data-[dir=ltr]:left-6 group-data-[dir=ltr]:right-6 group-data-[dir=rtl]:left-6 group-data-[dir=rtl]:right-6 lg:top-8 lg:w-[300px] lg:px-6 lg:py-3 lg:group-data-[dir=ltr]:left-10 lg:group-data-[dir=ltr]:right-auto lg:group-data-[dir=rtl]:left-auto lg:group-data-[dir=rtl]:right-10 kiosk:!left-[5%] kiosk:!right-auto kiosk:bottom-[calc(28%)] kiosk:top-auto kiosk:w-[90%] kiosk:border-0 kiosk:p-6 kiosk:px-12 kiosk:shadow-[0px_1px_6px_3px_rgba(0,0,0,0.5)]",
          routeFinish?.id && "hidden lg:flex",
        )}
        onClick={() => setOpen(true)}
      >
        <IoSearch className="text-xl text-primary group-data-[dir=ltr]:mr-2 group-data-[dir=rtl]:ml-2 lg:text-2xl kiosk:text-4xl kiosk:group-data-[dir=ltr]:mr-6 kiosk:group-data-[dir=rtl]:ml-6" />
        <span className="overflow-hidden text-ellipsis whitespace-nowrap kiosk:text-xl">
          {t("search-extended")}
        </span>
      </button>
      <CommandDialog
        dialogProps={{
          open: open,
          onOpenChange: openChangeHandler,
          showCloseButton: stops.length === 1,
        }}
        dialogContentProps={{
          onPointerDownOutside: (e) => {
            if (e.target && e.target instanceof HTMLElement) {
              if (e.target.parentElement?.id !== "root") {
                e.preventDefault();
              }
            }
          },
          onWheel: (e) => {
            e.stopPropagation();
          },
          onTouchMove: (e) => {
            e.stopPropagation();
          },
        }}
        dialogClassName={cn(
          "min-h-[326px] rounded-xl border-0 shadow-[0px_1px_6px_3px_rgba(0,0,0,0.5)] sm:top-[30%] lg:h-[580px] kiosk:bottom-[12%] kiosk:top-auto",
          mobileMode &&
            "left-2 right-2 top-2 w-auto max-w-none translate-x-0 translate-y-0 pt-5 sm:top-2",
          !multipointEnabled && "[&_[cmdk-input-wrapper]]:mb-4",
        )}
        commandProps={{ shouldFilter: false }}
      >
        {multipointEnabled && stops.length > 1 ? (
          <StopsContext
            stops={stops}
            focusedStop={focusedStop}
            onRemove={removeStopHandler}
            onStopsChanged={stopsChangedHandler}
            onFocusChange={stopFocusChangeHandler}
            type={"command"}
          />
        ) : (
          <CommandInput
            placeholder={`${t("search-extended")}`}
            value={search}
            onValueChange={onSearchHandler}
            maxLength={20}
          />
        )}
        {multipointEnabled && (
          <div className="mb-4 mt-2 flex flex-wrap items-center justify-center gap-4 group-data-[dir=ltr]:pr-5 group-data-[dir=rtl]:pl-5 lg:mt-4">
            <Button
              className="h-8 rounded-full px-8 text-xs lg:h-10 lg:text-base kiosk:h-14 kiosk:text-lg"
              onClick={addStopHandler}
              disabled={stops.length === 5}
            >
              <FaCirclePlus className="text-base group-data-[dir=ltr]:mr-2 group-data-[dir=rtl]:ml-2 kiosk:text-xl" />{" "}
              {t("add-stop")}
            </Button>
            {stops.length > 1 && (
              <>
                <Button
                  className="h-8 rounded-full px-8 text-xs lg:h-10 lg:text-base kiosk:h-14 kiosk:text-lg"
                  onClick={multipointDoneHandler}
                  disabled={stops.filter((stop) => !stop.poi).length > 0}
                >
                  <FaCircleCheck className="text-base group-data-[dir=ltr]:mr-2 group-data-[dir=rtl]:ml-2 kiosk:text-xl" />{" "}
                  {t("done")}
                </Button>
                {allowSave && mobileMode && (
                  <Button
                    className="h-8 basis-1/2 rounded-full px-8 text-xs lg:h-10 lg:text-base kiosk:h-14 kiosk:text-lg"
                    onClick={saveRouteHandler}
                    disabled={stops.filter((stop) => !stop.poi).length > 0}
                  >
                    <FaSave className="text-base group-data-[dir=ltr]:mr-2 group-data-[dir=rtl]:ml-2 kiosk:text-xl" />{" "}
                    {t("save-route")}
                  </Button>
                )}
              </>
            )}
          </div>
        )}
        <CommandList ref={listRef} scrollRef={scrollRef}>
          {(mobileMode || verticalKioskMode) && stops.length <= 1 && (
            <CommandGroup
              heading={t("categories")}
              className="text-sm lg:text-base kiosk:text-xl"
            >
              <ul className="mb-0 flex flex-wrap gap-2">
                {filterCategories
                  .filter((i) => i.listed !== false)
                  .map((category) => (
                    <li
                      key={category.id}
                      style={{
                        //@ts-expect-error color
                        "--category-color": `hsl(${category.color})`,
                      }}
                      className={cn(
                        "flex cursor-pointer items-center whitespace-nowrap rounded-full bg-primary px-3 py-1.5 text-white kiosk:px-6 kiosk:py-3 kiosk:text-base",
                        category.color && "bg-[var(--category-color)]",
                      )}
                      onClick={() => setFilterCategoryHandler(category)}
                    >
                      <span className="text-sm lg:text-lg">
                        {category.icon && React.createElement(category.icon)}
                      </span>
                      {category.iconImage && (
                        <img
                          src={
                            category.iconImageAlt
                              ? category.iconImageAlt
                              : category.iconImage
                          }
                          alt={t(category.title)}
                          className="w-[18px] kiosk:w-[24px]"
                        />
                      )}
                      {verticalKioskMode && (
                        <span className="group-data-[dir=ltr]:ml-2 group-data-[dir=rtl]:mr-2">
                          {t(category.title)}
                        </span>
                      )}
                    </li>
                  ))}
              </ul>
            </CommandGroup>
          )}
          {combiNavEnabled && (stops.length === 1 || !multipointEnabled) && (
            <CommandGroup className="text-sm lg:text-base kiosk:text-xl">
              <CommandItem
                className="cursor-pointer gap-1 aria-selected:text-foreground lg:aria-selected:text-primary"
                onSelect={navigateFromHomeHandler}
              >
                <TbHome />
                <span className="flex-1">{t("navigate-from-home")}</span>
              </CommandItem>
            </CommandGroup>
          )}
          <CommandEmpty className="text-sm lg:text-base kiosk:text-xl">
            {t("no-results")}
          </CommandEmpty>
          {/*(!multipointEnabled || stops.length === 1) &&
            (`${t("wifi")}`.toLowerCase().indexOf(search.toLowerCase()) > -1 ||
              `wifi`.indexOf(search.toLowerCase()) > -1) &&
            search.length > 0 && (
              <CommandGroup
                heading={t("other")}
                className="text-sm lg:text-base"
              >
                <CommandItem value="-" className="hidden" />
                <CommandItem
                  key={"wifi"}
                  value={"wifi"}
                  onSelect={() => {
                    onWifiHandler();
                  }}
                  className="cursor-pointer aria-selected:text-foreground lg:aria-selected:text-primary"
                >
                  <span className="flex-1">{t("wifi")}</span>
                </CommandItem>
              </CommandGroup>
            )*/}
          {(!multipointEnabled || stops.length === 1) &&
            getClosestList().length > 0 &&
            search.length > 0 && (
              <CommandGroup
                heading={`${t("see-all")}`}
                className="text-sm lg:text-base kiosk:text-xl"
              >
                <CommandItem value="-" className="hidden" />
                {getClosestList().map((item) => {
                  return (
                    <CommandItem
                      key={item.title}
                      value={item.title}
                      onSelect={() => {
                        onSeeAllHandler(item);
                      }}
                      className="cursor-pointer aria-selected:text-foreground lg:aria-selected:text-primary kiosk:py-1.5"
                    >
                      <span className="flex-1 capitalize">{`${t("all")} ${pluralize(t(item.title))}`}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          {foundInTitle.length > 0 && (
            <CommandGroup
              heading={t("suggestions")}
              className="pt-0 text-sm lg:text-base kiosk:text-xl"
            >
              {((mobileMode && enableGVision) ||
                (haveCustomPosition &&
                  stops.length > 1 &&
                  stops.filter((stop) => stop.poi?.id === "custom-position")
                    .length === 0)) && (
                <div
                  onClick={() => recognizeLocationHandler()}
                  className="flex cursor-pointer items-center text-primary hover:text-primary/70"
                >
                  <TbCurrentLocation className="h-6 w-6 group-data-[dir=ltr]:mr-4 group-data-[dir=rtl]:ml-4" />
                  {haveCustomPosition ? t("current-location") : t("locate-me")}
                </div>
              )}
              <CommandItem value="-" className="hidden" />
              {foundInTitle.map((poi) => {
                const amenity = amenities.find(
                  (a) => a.id === poi.properties.amenity,
                );
                if (import.meta.env.VITE_PROXIMIIO_BUNDLE_URL && amenity) {
                  amenity.icon = `${import.meta.env.VITE_PROXIMIIO_BUNDLE_URL}/amenities/${amenity.id}.png`;
                }
                return (
                  <CommandItem
                    key={poi.id}
                    value={poi.id}
                    onSelect={() => {
                      onSelectHandle({ poi });
                    }}
                    className="cursor-pointer aria-selected:text-foreground lg:aria-selected:text-primary kiosk:py-1.5"
                  >
                    {amenity && (
                      <img
                        src={amenity.icon}
                        alt={t(poi.properties.title)}
                        className="mx-2 max-h-5 max-w-5 lg:max-h-8 lg:max-w-8"
                      />
                    )}
                    <span className="flex-1">{poi.properties?.title}</span>
                    {/*<span className="font-bold">{poi.floorName}</span>*/}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}
          {foundInDescription.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup
                heading={t("found-in-description")}
                className="text-sm lg:text-base kiosk:text-xl"
              >
                <CommandItem value="-" className="hidden" />
                {foundInDescription.map((poi) => {
                  const amenity = amenities.find(
                    (a) => a.id === poi.properties.amenity,
                  );
                  if (import.meta.env.VITE_PROXIMIIO_BUNDLE_URL && amenity) {
                    amenity.icon = `${import.meta.env.VITE_PROXIMIIO_BUNDLE_URL}/amenities/${amenity.id}.png`;
                  }
                  return (
                    <CommandItem
                      key={poi.id}
                      value={poi.id}
                      onSelect={() => {
                        onSelectHandle({ poi });
                      }}
                      className="cursor-pointer aria-selected:text-foreground lg:aria-selected:text-primary kiosk:py-1.5"
                    >
                      {amenity && (
                        <img
                          src={amenity.icon}
                          alt={t(poi.properties.title)}
                          className="mx-2 max-h-5 max-w-5 lg:max-h-8 lg:max-w-8"
                        />
                      )}
                      <span className="flex-1">{poi.properties?.title}</span>
                      {/*<span className="font-bold">{poi.floorName}</span>*/}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}

export default PoiSearch;
